// utils & store
import { ampli } from '@/ampli';
import { getLocalTimeZone, ZonedDateTime, parseAbsoluteToLocal } from '@internationalized/date';
import { Close, Title } from '@radix-ui/react-dialog';
import { AriaDatePickerProps, AriaTimeFieldProps, useDatePicker } from '@react-aria/datepicker';
import { useDatePickerState } from '@react-stately/datepicker';
import { Dispatch, SetStateAction, useState, useRef, useEffect } from 'react';
import { shallow } from 'zustand/shallow';

import {
  expirationSelectors,
  LIMIT_ORDER_EXPIRY_KEY,
  LIMIT_ORDER_MAX_EXPIRATION_DAYS,
  useLimitOrderStore,
} from '@/store/limitOrder';

import { buttons } from '@/styles/buttons';
import { dialogCloseBtnStyles } from '@/styles/dialog';
import { fontStyles } from '@/styles/typography';

import { XIcon } from '@/ui/Icons/XIcon';
import { Modal } from '@/ui/Modal';

import { CalendarPopover } from '../../TransactionModule/LimitMode/ExpiryDropDownMenu/Calendar/CalendarPopover';
import { CalendarDateTimeField } from '../../TransactionModule/LimitMode/ExpiryDropDownMenu/DateTimeField';

interface CustomExpiryProps {
  /** Controls the visibility of the modal */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Function to toggle the visibility of the modal */
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

const getSelectedZonedDateTime = (initialValue?: Date) =>
  initialValue != null
    ? parseAbsoluteToLocal(initialValue.toISOString())
    : parseAbsoluteToLocal(new Date().toISOString()).add({ hours: 1 });

/**
 * Allows users to set custom limit order expiration dates.
 */
export const CustomExpiryModal = ({ isOpen, onOpenChange, onClose }: CustomExpiryProps) => {
  const nowDateTimeObjectLocalized = parseAbsoluteToLocal(new Date().toISOString());
  const usersLocalTimeZone = getLocalTimeZone().replaceAll('_', ' ');

  const { customExpiration, setCustomExpiration } = useLimitOrderStore(
    expirationSelectors,
    shallow,
  );

  const [selectedZonedDateTime, setSelectedZonedDateTime] = useState(
    getSelectedZonedDateTime(customExpiration),
  );

  // Update selected date on custom-expiration change
  useEffect(
    () => setSelectedZonedDateTime(getSelectedZonedDateTime(customExpiration)),
    [customExpiration],
  );

  // handlers
  const handleSaveCustomDate = () => {
    const normalizedNow = new Date().setSeconds(0, 0);
    const normalizedSelected = selectedZonedDateTime.set({ second: 0, millisecond: 0 }).toDate();
    const minutesInFuture = (normalizedSelected.valueOf() - normalizedNow) / (1000 * 60) || 1;

    localStorage.setItem(LIMIT_ORDER_EXPIRY_KEY, minutesInFuture.toString());
    setCustomExpiration(selectedZonedDateTime.toDate());
    ampli.limitOrderExpiryUpdated({ limit_order_expiry_type: 'custom' });
    onClose();
  };

  const handleDateTimeChange = (zonedDateTime: ZonedDateTime | null) => {
    if (zonedDateTime) {
      setSelectedZonedDateTime(zonedDateTime);
    }
  };

  // datepicker
  const state = useDatePickerState<ZonedDateTime>({
    onChange: handleDateTimeChange,
    value: selectedZonedDateTime,
    minValue: nowDateTimeObjectLocalized,
    maxValue: nowDateTimeObjectLocalized.add({ days: LIMIT_ORDER_MAX_EXPIRATION_DAYS }),
  });

  const dateTimeInputProps: AriaDatePickerProps<ZonedDateTime> = {
    'onChange': handleDateTimeChange,
    'value': selectedZonedDateTime,
    'minValue': nowDateTimeObjectLocalized,
    'maxValue': nowDateTimeObjectLocalized.add({ days: LIMIT_ORDER_MAX_EXPIRATION_DAYS }),
    'onBlur': () => state.close(),
    'aria-label': 'Date or Time input',
  };

  // calendar
  const calendarPopoverRef = useRef<HTMLDivElement>(null);
  const { calendarProps } = useDatePicker<ZonedDateTime>(
    dateTimeInputProps,
    state,
    calendarPopoverRef,
  );

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose}>
      <div className="py-5.5 px-6">
        <div className="flex items-center justify-between">
          <Title className={fontStyles({ variant: 'H5' })}>Set custom expiration date</Title>
          <Close className={dialogCloseBtnStyles().button({ absolute: true })} onClick={onClose}>
            <XIcon aria-hidden className={dialogCloseBtnStyles().icon()} />
          </Close>
        </div>
        <div className={fontStyles({ variant: 'BODY_MD', class: 'pb-1 pt-4 pr-8' })}>
          Choose a custom expiration date up to {LIMIT_ORDER_MAX_EXPIRATION_DAYS} days in the future
          for your limit order.
        </div>
        <CalendarDateTimeField
          state={state}
          datePickerProps={dateTimeInputProps}
          timeFieldProps={dateTimeInputProps as AriaTimeFieldProps<ZonedDateTime>}
          onClick={() => state.setOpen(!state.isOpen)}
        />
        {state.isOpen && (
          <CalendarPopover
            state={state}
            now={nowDateTimeObjectLocalized}
            calendarProps={calendarProps}
            popoverRef={calendarPopoverRef}
          />
        )}
        <div
          aria-hidden={!state.isInvalid}
          role="alert"
          className={fontStyles({ variant: 'BODY_SM', class: 'text-token-red-1000 min-h-7 mt-2' })}
        >
          {state.isInvalid &&
            `Matcha supports expiry dates up to ${LIMIT_ORDER_MAX_EXPIRATION_DAYS} days in the future only.`}
        </div>
        <div
          className={fontStyles({ variant: 'BODY_SM', class: 'text-token-gray-800 my-2' })}
        >{`${usersLocalTimeZone} time zone.`}</div>
      </div>
      <div className="flex items-center justify-end gap-2 p-4 w-full border-t border-special-border-divider">
        <button
          className={buttons({
            color: 'transparent',
            size: 'small',
            shape: 'rounded',
          })}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          disabled={state.isInvalid || selectedZonedDateTime === null}
          className={buttons({
            color: 'purple',
            size: 'small',
            shape: 'rounded',
            class: 'min-w-20',
          })}
          onClick={handleSaveCustomDate}
        >
          Save
        </button>
      </div>
    </Modal>
  );
};
